import { getAuthCookieOptions } from './cookieOptions'
import { RemixBrowserCookie } from './remixBrowserCookie'

const EMAIL_COOKIE_NAME = 'fd_email'
const ACCOUNT_ID_COOKIE_NAME = 'fd_account_id'
const ACCOUNT_NAME_COOKIE_NAME = 'fd_account_name'
const LOGIN_TIME_COOKIE_NAME = 'fd_login_time'
const ACCESS_TOKEN_COOKIE_NAME = 'fd_access_token'

class UserSession {
  static setEmail(email: string) {
    RemixBrowserCookie.set(EMAIL_COOKIE_NAME, email, getAuthCookieOptions())
  }

  static getEmail(): string | null {
    return RemixBrowserCookie.get<string>(EMAIL_COOKIE_NAME)
  }

  static setAccountId(accountId: string) {
    RemixBrowserCookie.set(
      ACCOUNT_ID_COOKIE_NAME,
      accountId,
      getAuthCookieOptions()
    )
  }

  static getAccountId(): string {
    const id = this.maybeGetAccountId()

    if (!id) {
      throw new Error('Account ID missing when expected to be present')
    }

    return id
  }

  static maybeGetAccountId(): string | null {
    return (
      RemixBrowserCookie.get<string>(ACCOUNT_ID_COOKIE_NAME)?.trim() ?? null
    )
  }

  static setAccountName(accountName: string) {
    RemixBrowserCookie.set(
      ACCOUNT_NAME_COOKIE_NAME,
      accountName,
      getAuthCookieOptions()
    )
  }

  static getAccountName(): string | null {
    return RemixBrowserCookie.get<string>(ACCOUNT_NAME_COOKIE_NAME)
  }

  static setLoginTime(time: Date = new Date()) {
    RemixBrowserCookie.set(
      LOGIN_TIME_COOKIE_NAME,
      time.toISOString(),
      getAuthCookieOptions()
    )
  }

  static getLoginTime(): Date | null {
    const timestamp = RemixBrowserCookie.get<string>(LOGIN_TIME_COOKIE_NAME)
    return timestamp ? new Date(timestamp) : null
  }

  static getSolutionId(): string {
    return 'b6987a9b-1e51-44c6-8406-ae5c766186ec'
  }

  static clearAll(): void {
    const expireOpts = { ...getAuthCookieOptions(), expires: new Date(0) }
    RemixBrowserCookie.set(EMAIL_COOKIE_NAME, '', expireOpts)
    RemixBrowserCookie.set(ACCOUNT_ID_COOKIE_NAME, '', expireOpts)
    RemixBrowserCookie.set(ACCOUNT_NAME_COOKIE_NAME, '', expireOpts)
    RemixBrowserCookie.set(LOGIN_TIME_COOKIE_NAME, '', expireOpts)
  }

  /**
   * Migrate auth data into per-key cookies (fd_email, fd_account_id,
   * fd_account_name) from the localStorage values used in earlier
   * versions of the app, plus the legacy `access_token` localStorage
   * entry into the `fd_access_token` cookie.
   *
   * For each cookie, only writes when it isn't already set so a value
   * populated by a sibling app on the parent domain (e.g. fd-manager,
   * orchestra) is not clobbered by a stale localStorage value. The
   * localStorage entries are removed unconditionally once read so the
   * migration is a no-op on subsequent loads.
   */
  static migrate(): void {
    const opts = getAuthCookieOptions()

    const legacyToken = localStorage.getItem('access_token')
    if (legacyToken) {
      const existingToken = RemixBrowserCookie.get<string>(
        ACCESS_TOKEN_COOKIE_NAME
      )
      if (!existingToken) {
        RemixBrowserCookie.set(ACCESS_TOKEN_COOKIE_NAME, legacyToken, opts)
      }
      localStorage.removeItem('access_token')
    }

    const legacyEmail = localStorage.getItem('email')
    if (legacyEmail) {
      if (this.getEmail() === null) {
        RemixBrowserCookie.set(EMAIL_COOKIE_NAME, legacyEmail, opts)
      }
      localStorage.removeItem('email')
    }

    const legacyAccountId = localStorage.getItem('accountId')?.trim()
    if (legacyAccountId) {
      if (this.maybeGetAccountId() === null) {
        RemixBrowserCookie.set(ACCOUNT_ID_COOKIE_NAME, legacyAccountId, opts)
      }
      localStorage.removeItem('accountId')
    }

    const legacyAccountName = localStorage.getItem('accountName')
    if (legacyAccountName) {
      if (this.getAccountName() === null) {
        RemixBrowserCookie.set(
          ACCOUNT_NAME_COOKIE_NAME,
          legacyAccountName,
          opts
        )
      }
      localStorage.removeItem('accountName')
    }
  }
}

export default UserSession
